/**
 * AttackRange — shared class→attack-range classification.
 *
 * Phase C.2 (2026-05-12) — replaces the duplicated `isRanged = ['hunter',
 * 'mage', 'rogue'].includes(playerClass)` mapping that previously lived
 * in BOTH:
 *   - src/game/systems/combat/CombatManager.ts:123 (client predictive hit-detect)
 *   - src/server/handlers/CombatDamageHandler.ts:142 (server anti-cheat gate)
 *
 * 2026-07-11 — the classification is now DERIVED from the generated TOML
 * content instead of a hardcoded class list. The old RANGED_HERO_CLASSES
 * array silently drifted from the ability data twice: warlock/druid got
 * ranged autos (range 500, tags ["ranged","basic"]) but stayed melee here
 * (server rejected their autos at 120+40px), and rogue was reworked into
 * a melee assassin (assassin_auto, tags ["melee","basic"]) but stayed
 * ranged here (860px anti-cheat envelope for a 150px melee auto). A class
 * is ranged iff one of its `basic`-tagged abilities carries an explicit
 * `ranged` tag — the same tags the content pipeline compiles from
 * content/abilities/*.toml. AttackRange.test.ts enforces that
 * every class has exactly one non-conflicting explicit melee/ranged
 * classification, so a forgotten tag fails the suite instead of silently
 * defaulting.
 *
 * The function returns the appropriate range constant for a hero class
 * given the active gameplay config; consumers add their own network-
 * jitter buffer on the server side (kept site-local since it's specific
 * to the anti-cheat path).
 */

import { GAMEPLAY_CONFIG } from '../constants/GameplayConfig';
import { ABILITY_DATA } from '../content/AbilityDataAdapter';

/**
 * class → is-ranged, derived once from the generated ability data. Only
 * basics with an explicit 'melee'/'ranged' tag participate (steady_shot
 * is basic-tagged but classification-neutral); a class with no explicitly
 * tagged basic defaults to melee.
 */
let rangedByClass: Map<string, boolean> | null = null;

function buildRangedByClass(): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const ability of Object.values(ABILITY_DATA)) {
    const { class: heroClass, tags } = ability as { class?: string; tags?: string[] };
    if (!heroClass || !tags?.includes('basic')) continue;
    if (tags.includes('ranged')) {
      map.set(heroClass, true);
    } else if (tags.includes('melee') && !map.get(heroClass)) {
      map.set(heroClass, false);
    }
  }
  return map;
}

/**
 * True if the given hero class uses ranged attacks (longer attackRange).
 * Accepts any string; classes without a ranged-tagged basic default to melee.
 */
export function isRangedClass(heroClass: string | null | undefined): boolean {
  if (!heroClass) return false;
  rangedByClass ??= buildRangedByClass();
  return rangedByClass.get(heroClass) ?? false;
}

/**
 * Effective attack range for a hero class.
 *
 * Defaults to the values in `GAMEPLAY_CONFIG`. Pass `config` to override
 * for tests, ability-test-rig overrides, or balance experiments.
 */
export function getAttackRange(
  heroClass: string | null | undefined,
  config: { attackRangeMelee: number; attackRangeRanged: number } = GAMEPLAY_CONFIG,
): number {
  return isRangedClass(heroClass) ? config.attackRangeRanged : config.attackRangeMelee;
}
